import React, { useState, useEffect } from 'react';
import {
    Box, TextField, MenuItem, Checkbox, FormControlLabel,
    Button, Paper, Stack, Alert
} from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

function calcularDiferencaFinal(base, fechamentoAbertura, funcionariosIguais) {
    const b = parseFloat(base) || 0;
    const fa = parseFloat(fechamentoAbertura) || 0;

    let result;

    if (!funcionariosIguais) {
        result = b;
    } else if (b === 0 && fa === 0) {
        result = 0;
    } else if (b === 0) {
        result = fa;
    } else if (fa === 0) {
        result = b;
    } else {
        const mesmoSinal = (b >= 0) === (fa >= 0);
        if (mesmoSinal) {
            result = b + fa;
        } else {
            result = Math.min(b, fa);
        }
    }

    return result >= -2.00 ? 0 : parseFloat(result.toFixed(2));
}

const toFloat = (val) => {
    const f = parseFloat(val);
    return isNaN(f) ? '' : f;
};

export default function CaixaForm() {
    const { authenticatedFetch } = useAuth();
    const { registroEmEdicao, handleSalvoOuCancelado } = useOutletContext();

    const [listaCaixas, setListaCaixas] = useState([]);
    const [listaFuncionarios, setListaFuncionarios] = useState([]);
    const [caixaAnterior, setCaixaAnterior] = useState(null);
    const [erro, setErro] = useState('');

    const formVazio = {
        caixa_data: new Date().toISOString().split('T')[0],
        caixa_numero_id: '',
        caixa_funcionario_id: '',
        caixa_hora_abertura: '',
        caixa_hora_fechamento: '',
        caixa_valor_abertura: '',
        caixa_valor_fechamento: '',
        caixa_diferença: '',
        caixa_novo_fechamento: '',
        caixa_nova_diferença: '',
        caixa_diferença_fechamento_abertura: '',
        caixa_diferença_final: '',
        caixa_quem_fechou: '',
        caixa_observacao: '',
        caixa_scaneado: false,
    };

    const [formData, setFormData] = useState(formVazio);

    // Busca listas
    useEffect(() => {
        const buscarListas = async () => {
            try {
                const [resCaixas, resFuncs] = await Promise.all([
                    authenticatedFetch(`${API_BASE_URL}/api/caixa-numeros/`),
                    authenticatedFetch(`${API_BASE_URL}/api/funcionarios-ativos/`),
                ]);
                if (resCaixas.ok) setListaCaixas(await resCaixas.json());
                if (resFuncs.ok) setListaFuncionarios(await resFuncs.json());
            } catch (err) {
                setErro('Erro ao carregar listas: ' + err.message);
            }
        };
        buscarListas();
    }, []);

    // Popula form ao editar ou limpa ao entrar em novo
    useEffect(() => {
        if (registroEmEdicao) {
            setFormData({
                caixa_data: registroEmEdicao.data
                    ? registroEmEdicao.data.split('/').reverse().join('-')
                    : new Date().toISOString().split('T')[0],
                caixa_numero_id:                     registroEmEdicao.caixa_id ?? '',
                caixa_funcionario_id:                registroEmEdicao.funcionario_id ?? '',
                caixa_hora_abertura:                 registroEmEdicao.hora_abertura ?? '',
                caixa_hora_fechamento:               registroEmEdicao.hora_fechamento ?? '',
                caixa_valor_abertura:                toFloat(registroEmEdicao.valor_abertura),
                caixa_valor_fechamento:              toFloat(registroEmEdicao.valor_fechamento),
                caixa_diferença:                     toFloat(registroEmEdicao.diferenca),
                caixa_novo_fechamento:               toFloat(registroEmEdicao.novo_fechamento),
                caixa_nova_diferença:                toFloat(registroEmEdicao.nova_diferenca),
                caixa_diferença_fechamento_abertura: toFloat(registroEmEdicao.fechamento_abertura),
                caixa_diferença_final:               toFloat(registroEmEdicao.diferenca_final),
                caixa_quem_fechou:                   registroEmEdicao.quem_fechou ?? '',
                caixa_observacao:                    registroEmEdicao.observacao ?? '',
                caixa_scaneado:                      registroEmEdicao.scan ?? false,
            });
        } else {
            setFormData(formVazio);
            setCaixaAnterior(null);
        }
    }, [registroEmEdicao]);

    // Busca caixa anterior quando data + caixa preenchidos
    useEffect(() => {
        if (!formData.caixa_numero_id || !formData.caixa_data) return;

        const buscarAnterior = async () => {
            try {
                const res = await authenticatedFetch(
                    `${API_BASE_URL}/api/caixa-anterior/?caixa_id=${formData.caixa_numero_id}&data=${formData.caixa_data}`
                );
                if (res.ok) {
                    const data = await res.json();
                    setCaixaAnterior(data.anterior);
                }
            } catch {}
        };

        buscarAnterior();
    }, [formData.caixa_numero_id, formData.caixa_data]);

    // Calcula fechamento_abertura e diferenca_final
    useEffect(() => {
        const abertura           = parseFloat(formData.caixa_valor_abertura) || 0;
        const fechamentoAnterior = parseFloat(caixaAnterior?.valor_fechamento) || 0;

        const base = formData.caixa_nova_diferença !== '' && formData.caixa_nova_diferença !== null
            ? parseFloat(formData.caixa_nova_diferença) || 0
            : parseFloat(formData.caixa_diferença) || 0;

        const fechamentoAbertura = caixaAnterior != null
            ? parseFloat((abertura - fechamentoAnterior).toFixed(2))
            : null;

        const funcionariosIguais = caixaAnterior == null
            || caixaAnterior.funcionario_id === parseInt(formData.caixa_funcionario_id);

        const diferencaFinal = calcularDiferencaFinal(base, fechamentoAbertura, funcionariosIguais);

        // Sempre limpa linhas [Atenção] antigas antes de recalcular
        let novaObservacao = formData.caixa_observacao
            .split('\n')
            .filter(linha => !linha.startsWith('[Atenção]'))
            .join('\n')
            .trim();

        // Adiciona novo alerta se funcionários diferentes e há diferença
        if (!funcionariosIguais && fechamentoAbertura !== 0 && caixaAnterior) {
            const nomeAtual = listaFuncionarios.find(
                f => f.id === parseInt(formData.caixa_funcionario_id)
            )?.nome ?? '?';

            const textoAlerta =
                `[Atenção] Caixa ${caixaAnterior.data} (${caixaAnterior.funcionario_nome} ` +
                `fechamento R$${fechamentoAnterior.toFixed(2)}), ` +
                `${formData.caixa_data} (${nomeAtual} abertura R$${abertura.toFixed(2)}), ` +
                `diferença R$${fechamentoAbertura.toFixed(2)}. Verificar responsabilidade.`;

            novaObservacao = novaObservacao
                ? `${novaObservacao}\n${textoAlerta}`
                : textoAlerta;
        }

        setFormData(prev => ({
            ...prev,
            caixa_diferença_fechamento_abertura: fechamentoAbertura != null
                ? parseFloat(fechamentoAbertura.toFixed(2))
                : '',
            caixa_diferença_final: diferencaFinal,
            caixa_observacao: novaObservacao,
        }));
    }, [
        formData.caixa_valor_abertura,
        formData.caixa_diferença,
        formData.caixa_nova_diferença,
        formData.caixa_funcionario_id,
        caixaAnterior,
    ]);

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

    const salvar = async () => {
        setErro('');
        try {
            const url = registroEmEdicao
                ? `${API_BASE_URL}/api/caixa/editar/${registroEmEdicao.id}/`
                : `${API_BASE_URL}/api/caixa-salvar/`;
            const method = registroEmEdicao ? 'PUT' : 'POST';
            const body = registroEmEdicao
                ? formData
                : { registros: [{ ...formData, key: 0 }] };
            
            console.log('Enviando:', JSON.stringify(body, null, 2));

            const response = await authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const data = await response.json();
            console.log('Resposta:', data); // ← e isso

            if (response.ok) {
                handleSalvoOuCancelado();
            } else {
                const data = await response.json();
                setErro(data.error || 'Erro ao salvar.');
            }
        } catch (err) {
            setErro('Erro ao salvar: ' + err.message);
        }
    };

    const funcionariosIguais = caixaAnterior == null
        || caixaAnterior.funcionario_id === parseInt(formData.caixa_funcionario_id);

    const fechamentoAberturaDiferente =
        !funcionariosIguais &&
        parseFloat(formData.caixa_diferença_fechamento_abertura) !== 0;

    return (
        <Paper sx={{ maxWidth: 900, p: 2, mt: 2 }}>
            <Stack spacing={2}>

                {erro && <Alert severity="error">{erro}</Alert>}

                {/* Identificação */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        sx={{ maxWidth: 150 }} label="Data" type="date" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData.caixa_data}
                        onChange={set('caixa_data')}
                    />
                    <TextField
                        sx={{ maxWidth: 160 }} select label="Caixa" fullWidth
                        value={formData.caixa_numero_id}
                        onChange={set('caixa_numero_id')}
                    >
                        <MenuItem value="">Selecione</MenuItem>
                        {listaCaixas.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        sx={{ maxWidth: 250 }} select label="Funcionário" fullWidth
                        value={formData.caixa_funcionario_id}
                        onChange={set('caixa_funcionario_id')}
                    >
                        <MenuItem value="">Selecione</MenuItem>
                        {listaFuncionarios.map(f => (
                            <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        sx={{ maxWidth: 140 }} label="Hora Abertura" placeholder="00:00" fullWidth
                        value={formData.caixa_hora_abertura}
                        onChange={set('caixa_hora_abertura')}
                    />
                    <TextField
                        sx={{ maxWidth: 160 }} label="Hora Fechamento" placeholder="00:00" fullWidth
                        value={formData.caixa_hora_fechamento}
                        onChange={set('caixa_hora_fechamento')}
                    />
                </Box>

                {/* Caixa anterior */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>Dados do caixa anterior:</span>
                    <TextField
                        label="Funcionário Anterior"
                        value={caixaAnterior?.funcionario_nome ?? ''}
                        disabled sx={{ maxWidth: 200 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Data Fechamento"
                        value={caixaAnterior?.data ?? ''}
                        disabled sx={{ maxWidth: 160 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Valor Fechamento"
                        value={caixaAnterior?.valor_fechamento ?? ''}
                        disabled sx={{ maxWidth: 160 }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* Financeiro Base */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <TextField
                        label="Valor Abertura" sx={{ maxWidth: 180 }} type="number"
                        inputProps={{ step: '0.01' }} fullWidth
                        value={formData.caixa_valor_abertura}
                        onChange={set('caixa_valor_abertura')}
                    />
                    <TextField
                        label="Valor Fechamento" sx={{ maxWidth: 180 }} type="number"
                        inputProps={{ step: '0.01' }} fullWidth
                        value={formData.caixa_valor_fechamento}
                        onChange={set('caixa_valor_fechamento')}
                    />
                    <TextField
                        label="Diferença Turno" sx={{ maxWidth: 180 }} type="number"
                        inputProps={{ step: '0.01' }} fullWidth
                        value={formData.caixa_diferença}
                        onChange={set('caixa_diferença')}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* Recontagem */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <TextField
                        label="Novo Fechamento (Recontagem)" type="number"
                        inputProps={{ step: '0.01' }} sx={{ maxWidth: 180 }}
                        value={formData.caixa_novo_fechamento}
                        onChange={set('caixa_novo_fechamento')}
                    />
                    <TextField
                        label="Nova Diferença" sx={{ maxWidth: 180 }} type="number"
                        inputProps={{ step: '0.01' }}
                        value={formData.caixa_nova_diferença}
                        onChange={set('caixa_nova_diferença')}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* Diferença fechamento anterior → abertura */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>Diferença fechamento anterior para abertura</span>
                    <TextField
                        label="Diferença" sx={{ maxWidth: 260 }} disabled
                        value={formData.caixa_diferença_fechamento_abertura}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            style: {
                                backgroundColor: fechamentoAberturaDiferente ? '#ffebee' : 'inherit',
                                borderRadius: 4,
                            }
                        }}
                    />
                </Box>

                {/* Alerta funcionários diferentes */}
                {fechamentoAberturaDiferente && (
                    <Alert severity="warning">
                        Funcionários diferentes entre o caixa anterior e o atual.
                        A diferença de fechamento/abertura não foi somada à diferença final.
                        Verifique a responsabilidade antes de salvar.
                    </Alert>
                )}

                {/* Diferença final */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <TextField
                        label="Diferença final" sx={{ maxWidth: 260 }} disabled
                        value={formData.caixa_diferença_final}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* Quem fechou */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <TextField
                        label="Quem Fechou" sx={{ maxWidth: 260 }}
                        value={formData.caixa_quem_fechou}
                        onChange={set('caixa_quem_fechou')}
                    />
                </Box>

                {/* Observações */}
                <TextField
                    label="Observações" multiline rows={4} fullWidth
                    value={formData.caixa_observacao}
                    onChange={set('caixa_observacao')}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.caixa_scaneado}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev, caixa_scaneado: e.target.checked
                                }))}
                            />
                        }
                        label="Documento Scaneado"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleSalvoOuCancelado}>
                            Cancelar
                        </Button>
                        <Button variant="contained" size="large" sx={{ px: 5 }} onClick={salvar}>
                            {registroEmEdicao ? 'Salvar Alterações' : 'Salvar'}
                        </Button>
                    </Box>
                </Box>

            </Stack>
        </Paper>
    );
}